import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button, Icon, ToastContext } from '@weezevent/nacre';
import { ModerationActions, ModerationTransitions } from '../../../../../../models';
import { pushOrSplice } from '../../../../../../utils/tools';

import TransitionActionCard from '../actions/ActionCard';

import Styles from '../transitionDetails.module.css';

export default function ActionsStep({ form, setForm, onCardDone, flowId, organization, setActionsInfos, transitionExists }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const [transitionActions, loadingTransitionActions] = ModerationActions.useApiModel(
        {
            organizationId: organization.id,
            flowId,
            transitionId: form.id
        },
        [organization, flowId, form]
    );

    const handleActionsList = React.useCallback(
        value => {
            const actions = [...form.actions];
            const initialAction = {
                action_type: '',
                type: '',
                name: '',
                description: '',
                optional_value: false,
                keep_form_answers: true,
                keep_membership_code: false,
                display: true
            };
            const new_actions = pushOrSplice(actions, initialAction, value);
            return setForm('actions', new_actions);
        },
        [form]
    );

    const handleAction = React.useCallback(
        (name, value, index) => {
            const actions = [...form.actions];
            const boolean_fields = ['optional_value', 'allow_replay', 'keep_form_answers', 'keep_membership_code'];

            if (name === 'item') {
                actions[index] = { ...value, display: true };
            } else {
                actions[index][name] = boolean_fields.includes(name) ? Boolean(value) : value?.toString();
                // Reset id to null on type change to ensure id relevance to the new action type.
                if (name === 'type') {
                    actions[index].id = null;
                }
            }

            setForm('actions', actions);
        },
        [form]
    );

    const handleSubmit = React.useCallback(
        async (isQuitting = false) => {
            let errors = [];
            let ids = [];
            const actions = form.actions.map(action => {
                let act = new ModerationActions(action);
                return act;
            });
            setIsSubmitting(true);

            const actionsPromise = actions.map(async action => {
                await action
                    .save({ organizationId: organization.id, flowId, transitionId: form.id })
                    .then(({ response }) => {
                        ids.push(response.id);
                        toast.success(t('ticket.moderation.statusesTransitions.transition.steps.settings.messageSuccessSave'));
                    })
                    .catch(() => {
                        errors.push(action);
                    });
            });

            Promise.all(actionsPromise).then(() => {
                setIsSubmitting(false);
                if (!errors.length) {
                    let transition = new ModerationTransitions({ ...form });
                    setActionsInfos(form.actions);
                    const actions = Array.from(new Set([...form.actions.map(ac => ac.id), ...ids])).filter(Boolean);
                    setForm('actions', actions);

                    transition.actions = actions;
                    transition.save({ organizationId: organization.id, flowId }).catch(() => {
                        setIsSubmitting(false);
                        return;
                    });

                    if (isQuitting) {
                        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flowId}/statuses_transitions/transitions`);
                    } else {
                        onCardDone(true);
                    }
                }
            });
        },
        [form, organization.id, flowId]
    );

    return (
        <>
            {form.actions.map((action, index) => (
                <TransitionActionCard
                    key={index}
                    form={form}
                    index={index}
                    action={action}
                    actions={form.actions}
                    handleAction={handleAction}
                    handleActionsList={handleActionsList}
                    transitionActions={transitionActions}
                    loadingTransitionActions={loadingTransitionActions}
                />
            ))}
            <div className={Styles['actions-addCta']} onClick={() => handleActionsList()}>
                <Icon name="plus" />
                <span>{t('ticket.moderation.statusesTransitions.transition.steps.actions.addCta')}</span>
            </div>
            {!transitionExists && (
                <Button.Group>
                    <Button
                        primary
                        label={t('ticket.moderation.statusesTransitions.transition.steps.settings.saveAndContinue')}
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting}
                    />
                    <Button
                        primary
                        inverted
                        label={t(`ticket.moderation.statusesTransitions.transition.steps.settings.saveAndQuit`)}
                        onClick={() => handleSubmit(true)}
                        disabled={isSubmitting}
                    />
                </Button.Group>
            )}
        </>
    );
}
